import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import React from 'react'
import { GeckoMeasurement } from 'types'

interface AssetSectionStats {
  sectionName: string
  minThickness: GeckoMeasurement
  averageThickness: GeckoMeasurement
  wallLossPercent: GeckoMeasurement
  nominalThickness?: GeckoMeasurement
  tMin?: GeckoMeasurement
}

const createTableStyles = (includesTMin: boolean) =>
  StyleSheet.create({
    tableContainer: {
      borderTop: '1px solid black',
      marginBottom: 24,
    },
    tableRow: {
      flexDirection: 'row',
      borderBottom: '1px solid black',
      minHeight: 15,
    },
    tableCell: {
      borderRight: '1px solid black',
      padding: 4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    tableCellNarrow: {
      width: includesTMin ? '16.67%' : '20%',
    },
    headerText: {
      fontSize: 9,
      fontWeight: 'bold',
    },
    cellText: {
      fontSize: 8,
    },
  })

interface SectionStatsRowProps {
  sectionStats: AssetSectionStats
  includesTMin: boolean
  tableStyles: ReturnType<typeof createTableStyles>
}

const SectionStatsRow: React.FC<SectionStatsRowProps> = ({
  sectionStats,
  includesTMin,
  tableStyles,
}) => {
  const { formatMeasurement } = useRugReportOps()

  return (
    <View style={tableStyles.tableRow}>
      <View
        style={[
          tableStyles.tableCell,
          tableStyles.tableCellNarrow,
          { borderLeft: '1px solid black' },
        ]}>
        <Text style={tableStyles.cellText}>{sectionStats.sectionName}</Text>
      </View>
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {sectionStats.nominalThickness
            ? formatMeasurement({
                measurement: sectionStats.nominalThickness,
                variant: 'narrow',
                minimumFractionDigits: 3,
              })
            : 'N/A'}
        </Text>
      </View>
      {includesTMin && (
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.cellText}>
            {sectionStats.tMin
              ? formatMeasurement({
                  measurement: sectionStats.tMin,
                  variant: 'narrow',
                  minimumFractionDigits: 3,
                })
              : 'N/A'}
          </Text>
        </View>
      )}
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {formatMeasurement({
            measurement: sectionStats.averageThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}
        </Text>
      </View>
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {formatMeasurement({
            measurement: sectionStats.minThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}
        </Text>
      </View>
      <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
        <Text style={tableStyles.cellText}>
          {formatMeasurement({
            measurement: sectionStats.wallLossPercent,
            variant: 'narrow',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      </View>
    </View>
  )
}

interface SectionStatsTableProps {
  assetSectionStats: AssetSectionStats[]
}

export const SectionStatsTable: React.FC<SectionStatsTableProps> = ({
  assetSectionStats,
}) => {
  // Check if any section has a T-Min value
  const includesTMin = assetSectionStats.some(
    (stats) => stats.tMin !== undefined
  )

  const tableStyles = createTableStyles(includesTMin)

  return (
    <View style={tableStyles.tableContainer}>
      <View style={[tableStyles.tableRow, { borderBottom: '2px solid black' }]}>
        <View
          style={[
            tableStyles.tableCell,
            tableStyles.tableCellNarrow,
            { borderLeft: '1px solid black' },
          ]}>
          <Text style={tableStyles.headerText}>Section</Text>
        </View>
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.headerText}>Nominal</Text>
        </View>
        {includesTMin && (
          <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
            <Text style={tableStyles.headerText}>T-Min</Text>
          </View>
        )}
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.headerText}>Average</Text>
        </View>
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.headerText}>Minimum</Text>
        </View>
        <View style={[tableStyles.tableCell, tableStyles.tableCellNarrow]}>
          <Text style={tableStyles.headerText}>Max Wall Loss</Text>
        </View>
      </View>
      {assetSectionStats.map((sectionStats) => (
        <SectionStatsRow
          key={sectionStats.sectionName}
          sectionStats={sectionStats}
          includesTMin={includesTMin}
          tableStyles={tableStyles}
        />
      ))}
    </View>
  )
}
